/**
 * Core hook for real-time task progress tracking.
 *
 * Uses WebSocket when the server accepts the subscription. HTTP polling always runs
 * for non-terminal tasks (slow when WS is live, faster when disconnected or ACL-denied).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StepDurationTracker } from "@/hooks/stepDurationTracker";
import { mergeTaskStatus } from "@/hooks/mergeTaskStatus";
import {
  HTTP_POLL_INTERVAL_MS,
  TERMINAL_TASK_STATUSES,
  clampTaskProgress,
  httpPollIntervalMs,
  idsNeedingHttpPoll as selectIdsNeedingHttpPoll,
} from "@/hooks/taskStreamHttpPoll";
import { getTaskStatus } from "@/services/api/tasks";
import type { SnapshotTaskStatus, TaskStep, TaskStepWithDuration } from "@/services/api/data-hub/types";
import { taskWebSocket } from "@/services/taskWebSocket";

export type { TaskStepWithDuration } from "@/services/api/data-hub/types";

const LOG = "[TaskStream]";
const TERMINAL_STATUSES = TERMINAL_TASK_STATUSES;
const STATUS_FALLBACKS: Record<string, string> = {
  pending: "Preparing task...",
  queued: "Waiting in queue...",
  running: "Processing...",
};

interface UseTaskStreamOptions {
  intervalMs?: number;
  onAllComplete?: () => void;
  onError?: (taskId: string, error: string) => void;
  /** Called on every WebSocket or HTTP poll status update (per task). */
  onStatusUpdate?: (update: SnapshotTaskStatus) => void;
  /** When set, only include steps from these task IDs (not from chained children). */
  stepTaskIds?: string[];
}

interface UseTaskStreamResult {
  /** @deprecated Use `isInProgress` — this does not mean HTTP polling is active. */
  isPolling: boolean;
  /** True while at least one tracked task is not terminal. */
  isInProgress: boolean;
  /** Active transport for in-flight tasks. */
  transport: "ws" | "http" | "idle";
  /** True when the HTTP poll fallback interval is running. */
  isHttpFallback: boolean;
  overallProgress: number;
  statusMessage: string;
  isComplete: boolean;
  hasFailed: boolean;
  isCancelled: boolean;
  steps: TaskStepWithDuration[];
  childTaskIds: string[];
  /** True until at least one status payload has been received for every tracked task. */
  isLoadingInitial: boolean;
  /** Tasks that reached a terminal status (completed, failed, cancelled, partial_failure). */
  finishedCount: number;
  totalCount: number;
}

function _short(id: string | undefined | null): string {
  return id ? id.slice(0, 8) : "?";
}

function normalizeTaskStatus(
  update: SnapshotTaskStatus | undefined | null,
  fallbackTaskId?: string,
): SnapshotTaskStatus | null {
  if (!update) {
    console.warn(LOG, "Ignoring empty status update");
    return null;
  }
  const taskId = update.task_id ?? fallbackTaskId;
  if (!taskId) {
    console.warn(LOG, "Ignoring status update without task_id");
    return null;
  }
  return taskId === update.task_id ? update : { ...update, task_id: taskId };
}

function _applyStatusUpdate(
  prev: Record<string, SnapshotTaskStatus>,
  update: SnapshotTaskStatus,
): Record<string, SnapshotTaskStatus> {
  return {
    ...prev,
    [update.task_id]: mergeTaskStatus(prev[update.task_id], update),
  };
}

export function useTaskStream(taskIds: string[], options: UseTaskStreamOptions = {}): UseTaskStreamResult {
  const { intervalMs = HTTP_POLL_INTERVAL_MS, onAllComplete, onError, onStatusUpdate, stepTaskIds } = options;

  const [statuses, setStatuses] = useState<Record<string, SnapshotTaskStatus>>({});
  const [wsLive, setWsLive] = useState(() => taskWebSocket.isLive);
  const [httpOnlyTaskIds, setHttpOnlyTaskIds] = useState<string[]>([]);
  const [httpPollActive, setHttpPollActive] = useState(false);
  const statusesRef = useRef(statuses);
  const onAllCompleteRef = useRef(onAllComplete);
  const onErrorRef = useRef(onError);
  const onStatusUpdateRef = useRef(onStatusUpdate);
  const unsubRef = useRef<(() => void) | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const trackerRef = useRef(new StepDurationTracker());

  onAllCompleteRef.current = onAllComplete;
  onErrorRef.current = onError;
  onStatusUpdateRef.current = onStatusUpdate;
  statusesRef.current = statuses;

  const taskIdsKey = taskIds.join(",");
  const pollInFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      unsubRef.current?.();
      unsubRef.current = null;
    };
  }, []);

  useEffect(() => {
    setWsLive(taskWebSocket.isLive);
    return taskWebSocket.onConnectionChange(() => {
      if (mountedRef.current) setWsLive(taskWebSocket.isLive);
    });
  }, []);

  const applyUpdate = useCallback((update: SnapshotTaskStatus, source: "ws" | "http", fallbackTaskId?: string) => {
    const normalized = normalizeTaskStatus(update, fallbackTaskId);
    if (!normalized || !mountedRef.current) return;
    console.debug(
      LOG,
      `(${source}) ${_short(normalized.task_id)} → ${normalized.status} ${normalized.progress ?? "?"}% "${normalized.status_message ?? ""}"`,
    );
    setStatuses((prev) => _applyStatusUpdate(prev, normalized));
    try {
      onStatusUpdateRef.current?.(normalized);
    } catch (e) {
      console.error(LOG, "onStatusUpdate callback threw:", e);
    }
  }, []);

  // --- WebSocket subscription ---
  useEffect(() => {
    if (taskIds.length === 0) {
      unsubRef.current?.();
      unsubRef.current = null;
      setHttpOnlyTaskIds([]);
      setStatuses({});
      trackerRef.current.reset();
      return;
    }

    trackerRef.current.reset();
    setHttpOnlyTaskIds([]);

    console.info(LOG, `Subscribing to ${taskIds.length} task(s): ${taskIds.map(_short).join(", ")}`);

    const handleDenied = (deniedIds: string[]) => {
      setHttpOnlyTaskIds((prev) => [...new Set([...prev, ...deniedIds])]);
      console.info(LOG, `WebSocket denied for ${deniedIds.length} task(s); HTTP poll will cover those`);
    };

    const prevUnsub = unsubRef.current;
    unsubRef.current = taskWebSocket.subscribe(
      taskIds,
      (update) => applyUpdate(update, "ws"),
      { onDenied: handleDenied },
    );
    prevUnsub?.();

    void (async () => {
      for (const id of taskIds) {
        if (!mountedRef.current) return;
        try {
          const result = await getTaskStatus(id);
          if (!mountedRef.current) return;
          applyUpdate(result, "http", id);
        } catch (e) {
          console.warn(LOG, `Bootstrap HTTP fetch failed for ${_short(id)}:`, e);
        }
      }
    })();

    // taskIdsKey tracks taskIds content; stable subscribe when IDs change without array identity churn
    // eslint-disable-next-line react-hooks/exhaustive-deps -- taskIdsKey
  }, [taskIdsKey, applyUpdate]);

  // --- HTTP poll backstop for all non-terminal tasks (interval only; bootstrap fetch is in WS effect) ---
  useEffect(() => {
    const trackedTaskIds = taskIdsKey ? taskIdsKey.split(",").filter(Boolean) : [];
    if (trackedTaskIds.length === 0) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setHttpPollActive(false);
      return;
    }

    let pollCount = 0;
    const pollIntervalMs = httpPollIntervalMs(taskWebSocket.isLive, intervalMs);

    function idsNeedingHttpPoll(): string[] {
      return selectIdsNeedingHttpPoll(trackedTaskIds, statusesRef.current);
    }

    async function fetchStatuses(reason: string) {
      const idsToFetch = idsNeedingHttpPoll();
      if (idsToFetch.length === 0) return;

      for (const id of idsToFetch) {
        if (!mountedRef.current) return;
        try {
          const result = await getTaskStatus(id);
          if (!mountedRef.current) return;
          applyUpdate(result, "http", id);
        } catch (e) {
          console.warn(LOG, `HTTP ${reason} failed for ${_short(id)}:`, e);
        }
      }
    }

    async function pollTick() {
      if (!mountedRef.current || pollInFlightRef.current) return;
      const ids = idsNeedingHttpPoll();
      if (ids.length === 0) {
        setHttpPollActive(false);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        return;
      }

      pollInFlightRef.current = true;
      try {
        pollCount++;
        if (pollCount === 1) {
          setHttpPollActive(true);
          console.info(LOG, `HTTP poll backstop every ${pollIntervalMs}ms for ${ids.length} task(s)`);
        } else {
          console.debug(LOG, `HTTP poll #${pollCount}`);
        }
        await fetchStatuses("poll");
      } finally {
        pollInFlightRef.current = false;
      }
    }

    if (idsNeedingHttpPoll().length > 0) {
      setHttpPollActive(true);
      pollRef.current = setInterval(() => void pollTick(), pollIntervalMs);
    } else {
      setHttpPollActive(false);
    }

    return () => {
      pollInFlightRef.current = false;
      setHttpPollActive(false);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [taskIdsKey, intervalMs, wsLive, applyUpdate]);

  // --- Derived state ---
  const values = taskIds.map((id) => statuses[id]).filter((s): s is SnapshotTaskStatus => s !== undefined);
  const receivedAll = values.length === taskIds.length;
  const isLoadingInitial = taskIds.length > 0 && !receivedAll;
  const allTerminal = taskIds.length > 0 && receivedAll && values.every((s) => TERMINAL_STATUSES.has(s.status));
  const isInProgress = taskIds.length > 0 && !allTerminal;
  const isPolling = isInProgress;
  const isHttpFallback = httpPollActive && !taskWebSocket.isLive;
  const transport: "ws" | "http" | "idle" =
    taskIds.length === 0
      ? "idle"
      : taskWebSocket.isLive
        ? "ws"
        : isHttpFallback
          ? "http"
          : "idle";
  const isComplete = taskIds.length > 0 && receivedAll && values.every((s) => s.status === "completed");
  const hasFailed = values.some((s) => s.status === "failed");
  const isCancelled = values.some((s) => s.status === "cancelled");
  const finishedCount = values.filter((s) => TERMINAL_STATUSES.has(s.status)).length;
  const progressSources = stepTaskIds ? stepTaskIds.map((id) => statuses[id]).filter(Boolean) : values;
  const overallProgress =
    progressSources.length > 0
      ? clampTaskProgress(progressSources.reduce((sum, s) => sum + (s.progress ?? 0), 0) / progressSources.length)
      : 0;

  const activeTask = values.find((s) => !TERMINAL_STATUSES.has(s.status));
  const latestTask = activeTask ?? values[0];
  const statusMessage = latestTask?.status_message || STATUS_FALLBACKS[latestTask?.status ?? ""] || "";

  // --- Completion / error callbacks ---
  const hasCalledRef = useRef(false);

  useEffect(() => {
    if (!allTerminal || hasCalledRef.current) return;
    hasCalledRef.current = true;

    const failedTasks = values.filter((s) => s.status === "failed");
    if (failedTasks.length > 0) {
      console.warn(LOG, `${failedTasks.length}/${taskIds.length} task(s) failed`);
      for (const s of failedTasks) {
        if (s.error) {
          try {
            onErrorRef.current?.(s.task_id, s.error);
          } catch (e) {
            console.error(LOG, "onError callback threw:", e);
          }
        }
      }
    } else if (values.some((s) => s.status === "cancelled")) {
      console.info(LOG, `${taskIds.length} task(s) terminal (some cancelled)`);
    } else {
      console.info(LOG, `All ${taskIds.length} task(s) completed`);
      try {
        onAllCompleteRef.current?.();
      } catch (e) {
        console.error(LOG, "onAllComplete callback threw:", e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allTerminal is derived from values/taskIds
  }, [allTerminal]);

  useEffect(() => {
    hasCalledRef.current = false;
  }, [taskIdsKey]);

  const stepSourceIds = stepTaskIds ?? taskIds;
  const combinedRawSteps: TaskStep[] = [];
  for (const id of stepSourceIds) {
    const s = statuses[id];
    if (s?.steps) combinedRawSteps.push(...s.steps);
  }

  const stepsKey = combinedRawSteps.length > 0 ? JSON.stringify(combinedRawSteps) : "[]";

  const steps: TaskStepWithDuration[] = useMemo(() => {
    return trackerRef.current.update(combinedRawSteps);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- combinedRawSteps serialized as stepsKey
  }, [stepsKey]);

  const seenChildIdsRef = useRef(new Set<string>());
  const childTaskIdsKey = values.map((s) => s.child_task_ids?.join(",") ?? "").join("|");
  const childTaskIds = useMemo(() => {
    const ids: string[] = [];
    for (const s of values) {
      if (s.child_task_ids?.length) ids.push(...s.child_task_ids);
    }

    const newIds = ids.filter((id) => !seenChildIdsRef.current.has(id));
    if (newIds.length > 0) {
      for (const id of newIds) seenChildIdsRef.current.add(id);
      console.info(LOG, `Discovered ${newIds.length} new child task(s): ${newIds.map(_short).join(", ")}`);
    }

    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- values serialized as childTaskIdsKey
  }, [childTaskIdsKey]);

  return {
    isPolling,
    isInProgress,
    transport,
    isHttpFallback,
    overallProgress,
    statusMessage,
    isComplete,
    hasFailed,
    isCancelled,
    steps,
    childTaskIds,
    isLoadingInitial,
    finishedCount,
    totalCount: taskIds.length,
  };
}
